import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getEventById, getEventTicketSales } from "../../services/eventService";
import { getTicketBuyers } from "../../services/attendeeService";
import {
  formatCurrency,
  formatDate,
  formatDateTime,
} from "../../utils/formatters";
import PageHeader from "../../components/common/PageHeader";
import Badge from "../../components/common/Badge";
import Avatar from "../../components/common/Avatar";
import StatCard from "../../components/common/StatCard";
import {
  ArrowLeft,
  Calendar,
  MapPin,
  Ticket,
  Package,
  TrendingUp,
  CircleDollarSign,
  Users,
  CheckCircle2,
  Clock,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const statusConfig = {
  0: { label: "Upcoming", variant: "info" },
  1: { label: "Live", variant: "success" },
  2: { label: "Past", variant: "default" },
};

const ProgressBar = ({ sold, total }) => {
  const pct = total > 0 ? Math.min((sold / total) * 100, 100) : 0;
  const color = pct >= 90 ? "bg-secondary" : "bg-primary";
  return (
    <div className="w-full bg-bg-base rounded-full h-2 mt-1.5">
      <div
        className={`h-2 rounded-full ${color} transition-all`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

const AttendeeRow = ({ buyer }) => {
  const [expanded, setExpanded] = useState(false);
  const extras = buyer.extras?.$values ?? buyer.extras ?? [];
  const hasExtras = extras.length > 0;

  return (
    <div className="border-b border-border last:border-0">
      <div
        className={`grid grid-cols-[2fr_1fr_1fr_1fr] gap-4 items-center px-5 py-3 hover:bg-bg-elevated transition ${
          hasExtras ? "cursor-pointer" : ""
        }`}
        onClick={() => hasExtras && setExpanded((p) => !p)}
      >
        {/* Attendee */}
        <div className="flex items-center gap-3 min-w-0">
          <Avatar
            name={buyer.user?.name}
            surname={buyer.user?.surname}
            image={buyer.user?.image}
            size="sm"
          />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm text-text-primary font-medium truncate">
                {buyer.user?.name} {buyer.user?.surname}
              </p>
              {hasExtras && (
                <span className="flex-shrink-0 text-xs bg-secondary/10 text-secondary border border-secondary/20 px-2 py-0.5 rounded-full font-medium">
                  +{extras.length} extra{extras.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>
            <p className="text-xs text-text-muted truncate">
              {buyer.user?.email}
            </p>
          </div>
        </div>

        {/* Ticket */}
        <div className="text-xs text-text-muted truncate">
          {buyer.ticketName ?? "—"}
        </div>

        {/* Scanned At */}
        <div className="text-xs text-text-muted">
          {buyer.scannedAt ? formatDateTime(buyer.scannedAt) : "—"}
        </div>

        {/* Status */}
        <div className="flex items-center justify-end gap-2">
          {buyer.isScanned ? (
            <span className="flex items-center gap-1 text-xs text-primary font-medium">
              <CheckCircle2 size={12} /> Scanned
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-text-muted">
              <Clock size={12} /> Pending
            </span>
          )}
          {hasExtras && (
            <span className="text-text-muted">
              {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </span>
          )}
        </div>
      </div>

      {/* Expanded extras */}
      {expanded && hasExtras && (
        <div className="px-5 pb-3 pt-2 bg-bg-elevated border-t border-border">
          <p className="text-xs text-text-muted mb-2">Extras purchased</p>
          <div className="flex flex-wrap gap-2">
            {extras.map((extra, i) => (
              <span
                key={i}
                className="text-xs bg-secondary/10 text-secondary border border-secondary/20 px-2.5 py-0.5 rounded-full"
              >
                {extra.eventExtra?.name ?? "Extra"}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const EventDetail = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [sales, setSales] = useState(null);
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buyersLoading, setBuyersLoading] = useState(true);

  useEffect(() => {
    if (!eventId) return;
    const fetch = async () => {
      setLoading(true);
      try {
        const [e, s] = await Promise.all([
          getEventById(eventId),
          getEventTicketSales(eventId),
        ]);
        setEvent(e);
        setSales(s);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [eventId]);

  useEffect(() => {
    if (!eventId) return;
    const fetch = async () => {
      setBuyersLoading(true);
      try {
        const res = await getTicketBuyers({ eventId, pageSize: 50 });
        setBuyers(res.data);
      } finally {
        setBuyersLoading(false);
      }
    };
    fetch();
  }, [eventId]);

  if (loading)
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-48 bg-bg-surface rounded-2xl" />
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-24 bg-bg-surface rounded-2xl" />
          ))}
        </div>
      </div>
    );

  if (!event)
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <p className="text-text-muted text-sm">Event not found.</p>
      </div>
    );

  const status = statusConfig[event.status] ?? statusConfig[0];
  const scannedCount = buyers.filter((b) => b.isScanned).length;

  const statCards = [
    {
      icon: <Ticket size={16} className="text-primary" />,
      label: "Tickets Sold",
      value: sales?.ticketsSold?.toLocaleString() ?? "0",
      accent: "bg-primary/10",
    },
    {
      icon: <Users size={16} className="text-tertiary" />,
      label: "Scanned In",
      value: `${scannedCount} / ${buyers.length}`,
      accent: "bg-tertiary/10",
    },
    {
      icon: <TrendingUp size={16} className="text-secondary" />,
      label: "Total Revenue",
      value: formatCurrency(sales?.totalRevenue ?? 0),
      accent: "bg-secondary/10",
    },
    // {
    //   icon: <CircleDollarSign size={16} className="text-primary" />,
    //   label: "Ingroove Fees",
    //   value: formatCurrency(sales?.ingrooveFeesCollected ?? 0),
    //   accent: "bg-primary/10",
    // },
  ];

  return (
    <div className="space-y-6">
      {/* Back */}
      <button
        onClick={() => navigate("/events")}
        className="flex items-center gap-1.5 text-xs text-text-muted hover:text-primary transition"
      >
        <ArrowLeft size={14} /> Back to Events
      </button>

      {/* Hero banner */}
      <div className="relative h-52 bg-bg-elevated rounded-2xl overflow-hidden">
        {event.image ? (
          <img
            src={event.image}
            alt={event.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Calendar size={40} className="text-border-hover" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute bottom-4 left-5 right-5">
          <div className="flex items-end justify-between">
            <div>
              <h1 className="text-xl font-bold text-white mb-1">
                {event.title}
              </h1>
              <div className="flex items-center gap-3 text-white/70 text-xs">
                {event.dates?.[0]?.date && (
                  <span className="flex items-center gap-1">
                    <Calendar size={11} />
                    {formatDate(event.dates[0].date)}
                  </span>
                )}
                {event.address?.displayName && (
                  <span className="flex items-center gap-1">
                    <MapPin size={11} />
                    {event.address.displayName}
                  </span>
                )}
              </div>
            </div>
            <Badge label={status.label} variant={status.variant} />
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {statCards.map((s) => (
          <StatCard key={s.label} {...s} />
        ))}
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left — tickets + extras */}
        <div className="space-y-4">
          {/* Tickets */}
          <div className="bg-bg-surface border border-border rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Ticket size={15} className="text-primary" />
              <h3 className="text-sm font-semibold text-text-primary">
                Tickets
              </h3>
            </div>
            {event.tickets?.length === 0 ? (
              <p className="text-xs text-text-muted">No tickets</p>
            ) : (
              <div className="space-y-4">
                {event.tickets?.map((ticket) => (
                  <div key={ticket.id}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-text-secondary font-medium">
                        {ticket.name}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-text-muted">
                          {ticket.sold ?? 0} / {ticket.quantity}
                        </span>
                        {ticket.isSoldOut && (
                          <Badge label="Sold out" variant="warning" />
                        )}
                      </div>
                    </div>
                    <ProgressBar
                      sold={ticket.sold ?? 0}
                      total={ticket.quantity}
                    />
                    <div className="flex items-center justify-between mt-1.5 text-xs text-text-muted">
                      <span>
                        {formatCurrency(ticket.hubBasePrice)} per ticket
                      </span>
                      {/* <span>{formatCurrency(ticket.price)} user price</span> */}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Extras */}
          {event.extras?.length > 0 && (
            <div className="bg-bg-surface border border-border rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Package size={15} className="text-secondary" />
                <h3 className="text-sm font-semibold text-text-primary">
                  Extras
                </h3>
              </div>
              <div className="space-y-4">
                {event.extras.map((extra) => (
                  <div key={extra.id}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-text-secondary font-medium">
                        {extra.name}
                      </span>
                      <span className="text-text-muted">
                        {extra.sold ?? 0} / {extra.quantity}
                      </span>
                    </div>
                    <ProgressBar
                      sold={extra.sold ?? 0}
                      total={extra.quantity}
                    />
                    <p className="text-xs text-text-muted mt-1">
                      {formatCurrency(extra.price)} per extra
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Financial summary */}
          <div className="bg-bg-surface border border-border rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp size={15} className="text-primary" />
              <h3 className="text-sm font-semibold text-text-primary">
                Financial Summary
              </h3>
            </div>
            <div className="space-y-0">
              {[
                {
                  label: "Ticket Revenue",
                  value: formatCurrency(sales?.ticketRevenue ?? 0),
                },
                {
                  label: "Extras Revenue",
                  value: formatCurrency(sales?.extrasRevenue ?? 0),
                },
                // {
                //   label: "Ingroove Fees",
                //   value: formatCurrency(sales?.ingrooveFeesCollected ?? 0),
                // },
              ].map((row) => (
                <div
                  key={row.label}
                  className="flex justify-between items-center py-2.5 border-b border-border last:border-0 text-xs"
                >
                  <span className="text-text-muted">{row.label}</span>
                  <span className="text-text-secondary font-medium">
                    {row.value}
                  </span>
                </div>
              ))}
              <div className="flex justify-between items-center pt-3 text-sm">
                <span className="text-text-muted font-medium">
                  Total Revenue
                </span>
                <span className="text-primary font-bold">
                  {formatCurrency(sales?.totalRevenue ?? 0)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right — attendees */}
        <div className="lg:col-span-2 bg-bg-surface border border-border rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Users size={15} className="text-primary" />
              <h3 className="text-sm font-semibold text-text-primary">
                Attendees
              </h3>
            </div>
            <div className="flex items-center gap-2 text-xs text-text-muted">
              <span className="text-primary font-medium">{scannedCount}</span>
              scanned of
              <span className="font-medium text-text-secondary">
                {buyers.length}
              </span>
            </div>
          </div>

          {buyersLoading ? (
            <div className="space-y-0">
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 px-5 py-3 border-b border-border animate-pulse"
                >
                  <div className="w-7 h-7 rounded-full bg-bg-elevated flex-shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 bg-bg-elevated rounded w-32" />
                    <div className="h-2.5 bg-bg-elevated rounded w-48" />
                  </div>
                </div>
              ))}
            </div>
          ) : buyers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Users size={28} className="text-border-hover mb-2" />
              <p className="text-xs text-text-muted">No attendees yet</p>
            </div>
          ) : (
            <div className="overflow-y-auto max-h-[600px]">
              {buyers.map((buyer) => (
                <AttendeeRow key={buyer.id} buyer={buyer} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventDetail;
